import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import AlertIcon from '@assets/img/icons/alert.svg?react';
import ChevronIcon from '@assets/img/icons/chevron.svg?react';
import { getIcon } from '@common/lib/getIcon';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import { type StorageItemProps } from '@common/types/components';
import { storageTextObj } from '@components/helpers/ObjectsText';
import { Picture } from '@components/ui/Picture/Picture';
import { useModal } from '@context/ModalContext';
import cn from 'classnames';
import { transform } from 'framer-motion';

import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { minus } from '@/common/lib/decimal';
import { playSound } from '@/common/lib/soundEffect';
import { toCapitalizeStr } from '@/common/lib/toCapitalizeStr';
import ContractBackground from '@/components/common/ContractBackground/ContractBackground';
import { StringMain, StringXSmall } from '@/components/Typography/Typography';
import { Scale } from '@/components/ui/Scale/Scale';
import { selectProductOfDayByGoodsType } from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const StorageItem: FC<StorageItemProps> = ({
  value,
  costPerUnit,
  gameItem,
  storageLimit,
}) => {
  const { t } = useTranslation();
  const { sellModal } = useModal();
  const image = getIcon(gameItem);
  const text = storageTextObj(t);
  const productOfDay = useAppSelector((state) =>
    selectProductOfDayByGoodsType(state, gameItem),
  );

  const calcPersentageFill = transform([0, storageLimit || 100], [0, 100]);
  const calcOverflowPersentage = transform([0, value], ['0%', '100%']);

  const achieveValue =
    storageLimit && value > storageLimit
      ? minus(value, storageLimit)
      : undefined;

  return (
    <div
      className={`storage-card overflow-hidden ${!(costPerUnit * value) ? 'card-disable' : ''} !h-16 pl-1`}
      data-fancybox
      data-src="#sell-modal"
      onClick={() => {
        playSound('buttons/proceed');
        sellModal({
          key: gameItem,
          value: { value, costPerUnit },
          achieveValue,
        });
      }}
    >
      <div className="storage-card__icon relative">
        {image && <Picture img={image.png} webp={image?.webp} alt={gameItem} />}
        {productOfDay && (
          <ContractBackground
            type={productOfDay?.type === 'premium' ? 'purple' : 'green'}
          />
        )}
      </div>
      <div className="storage-card__line">
        <div className="flex flex-col gap-1 w-[calc(100%_-_130px)]">
          <div className="w-full flex items-center justify-between">
            <StringXSmall className=" !text-white/40">
              {text[gameItem]}
            </StringXSmall>
            <StringXSmall className=" !text-white/40">
              {toCapitalizeStr(t('capacity'))}
            </StringXSmall>
          </div>
          <div className="w-full flex items-center justify-between">
            <StringMain weight="bold" className="!leading-1.2">
              {toShortNumFormat(value)}
            </StringMain>
            <div className="flex items-center relative">
              <StringMain
                weight="bold"
                className="!text-white/40 mr-[2px] !!leading-1.2"
              >
                {storageLimit ? toShortNumFormat(storageLimit) : ''}
              </StringMain>
              {!!storageLimit && value > storageLimit && (
                <>
                  <StringMain
                    weight="bold"
                    className="!text-white/40 mr-[2px] !leading-1.2"
                  >
                    /
                  </StringMain>
                  <StringMain
                    weight="bold"
                    className={cn(
                      '!text-[#FFE6E6] !leading-1.2',
                      css.textShadow,
                    )}
                  >
                    {toShortNumFormat(minus(value, storageLimit))}
                  </StringMain>
                  <AlertIcon className="w-4 h-4 min-w-4 min-h-4 shrink-0 absolute top-[15%] -right-5" />
                </>
              )}
            </div>
          </div>
          <div className="w-full flex gap-[2px]">
            <Scale
              className="flex-1 !w-auto"
              height={convertRemToPixels(0.25)}
              percentage={calcPersentageFill(value)}
            />
            {!!storageLimit && value > storageLimit && (
              <div
                style={{
                  height: 4,
                  width: calcOverflowPersentage(minus(value, storageLimit)),
                }}
                className="rounded-lg bg-red"
              ></div>
            )}
          </div>
        </div>
        {/* <div className="storage-card__item-sm">
          <span className="storage-card__item-sm-amount">
            {toShortNumFormat(value)}
          </span>
          <span>{text[gameItem]}</span>
        </div> */}
        <div className="storage-card__item">
          <div className="storage-card__item-block items-center justify-end">
            <picture>
              <source srcSet="img/icons/cash.webp" type="image/webp" />
              <img
                src="img/icons/cash.png"
                alt="cash"
                width={convertRemToPixels(1.5)}
              />
            </picture>
            <span className="storage-card__item-amount">
              {toShortNumFormat(costPerUnit * value)}
            </span>
            <div>
              <span className="storage-card__item-cash">Cash</span>
            </div>
          </div>
          <ChevronIcon className="text-white/40 w-6 h-6" />
          {/* <svg
            className="icon-vector"
            viewBox="0 0 18 32"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity: 0.4,
              fill: 'var(--color1, #fff)',
              width: '7.5px',
            }}
          >
            <path d="M1.074 1.074c0.669-0.669 1.755-0.669 2.424 0l13.714 13.714c0.669 0.669 0.669 1.755 0 2.424l-13.714 13.714c-0.67 0.669-1.755 0.669-2.424 0s-0.669-1.755 0-2.424l12.502-12.502-12.502-12.502c-0.669-0.669-0.669-1.755 0-2.424z" />
          </svg> */}
        </div>
      </div>
    </div>
  );
};

export { StorageItem };
